'use client';

import type { ReactNode } from 'react';

import type { WithID } from '@ps/models/lib/base';
import type { Listing } from '@ps/models/lib/listing';

import {
  VariantQuantityContext,
  type VariantQuantityContextValue,
} from '../contexts/variant-quantity/context';
import { useQuantitySelection } from '../hooks/useQuantitySelection';
import { useVariantSelection } from '../hooks/useVariantSelection';

type PdpInteractiveProps = {
  listing: WithID<Listing>;
  children: ReactNode;
};

/**
 * Client island that owns the PDP's variant + quantity state and shares it
 * via context to the variant-reactive leaves (`VariantAwarePrice`,
 * `VariantPickerRow`, `QuantityRow`, `BuyNowButton`), which render in
 * non-contiguous layout positions.
 *
 * Wraps the dumb view as `children` so the rest of the tree — including the
 * server-streamed seller card — stays server-rendered; only the small leaves
 * that read the context are client components.
 */
export function PdpInteractive({ listing, children }: PdpInteractiveProps) {
  const { selection, selectedVariant, onSelectAttr, hasVariants } =
    useVariantSelection(listing);

  const { quantity, setQuantity, maxQuantity, isFixedToOne, isSoldOut } =
    useQuantitySelection(listing, selectedVariant);

  const value: VariantQuantityContextValue = {
    selection,
    selectedVariant,
    onSelectAttr,
    hasVariants,
    quantity,
    maxQuantity,
    isFixedToOne,
    isSoldOut,
    setQuantity,
  };

  return (
    <VariantQuantityContext.Provider value={value}>
      {children}
    </VariantQuantityContext.Provider>
  );
}
